import { Button } from "antd";
import { useMemo, useRef, useState } from "react";

interface FormErrors {
    name?: string;
    email?: string;
    phone?: string;
    message?: string;
}

export default function ItemContactForm({ item, setFormVisible, messageApi }: { item: any, setFormVisible: React.Dispatch<React.SetStateAction<boolean>>, messageApi: any }) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [touched, setTouched] = useState<Record<string, boolean>>({});    
    const [formData, setFormData] = useState<any>({
        subject: `Inquiry for item: [${item?.GPID || ''}] ${item?.Name || ''}`,
        name: '',
        email: '',
        phone: '',
        message: '',
        source: window?.location?.href || 'https://gandpconstruction.com/inventory'
    });
    const formRef = useRef<HTMLFormElement>(null);    

    // Validation rules
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone: string): boolean => {
        if (!phone) return true; // Phone is optional
        const phoneRegex = /^[\d\s\-\+\(\)]{7,20}$/;
        return phoneRegex.test(phone);
    };

    const validateName = (name: string): boolean => {
        return name.trim().length >= 2;
    };

    const validateMessage = (message: string): boolean => {
        return message.trim().length >= 10;
    };

    // Get validation errors
    const errors = useMemo<FormErrors>(() => {
        const errs: FormErrors = {};

        if (!validateName(formData.name)) {
            errs.name = 'Name must be at least 2 characters';
        }

        if (!formData.email) {
            errs.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            errs.email = 'Please enter a valid email address';
        }

        if (formData.phone && !validatePhone(formData.phone)) {
            errs.phone = 'Please enter a valid phone number';
        }

        if (!formData.message) {
            errs.message = 'Message is required';
        } else if (!validateMessage(formData.message)) {
            errs.message = 'Message must be at least 10 characters';
        }

        return errs;
    }, [formData]);

    const isValid = useMemo(() => {
        return Object.keys(errors).length === 0;
    }, [errors]);

    const handleBlur = (field: string) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    const getInputClassName = (field: keyof FormErrors, hasError: boolean) => {
        const baseClasses = "w-full rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 outline-none";
        const normalClasses = "border border-gray-300 focus:border-brand focus:ring-2 focus:ring-brand/30";
        const errorClasses = "border-2 border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-200";
        const validClasses = "border border-green-400 bg-green-50 focus:border-green-500 focus:ring-2 focus:ring-green-200";

        if (touched[field]) {
            if (hasError) return `${baseClasses} ${errorClasses}`;
            if (formData[field]) return `${baseClasses} ${validClasses}`;
        }
        return `${baseClasses} ${normalClasses}`;
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Mark all fields as touched
        setTouched({ name: true, email: true, phone: true, message: true });

        if (!isValid) {
            messageApi.error('Please fix the errors in the form before submitting.');
            return;
        }

        setIsProcessing(true);

        try {
            const response = await fetch('/api/items/inquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                messageApi.success('Your message has been sent!', 5);
                setFormData({ name: '', email: '', phone: '', message: '', subject: `Inquiry for item: [${item?.GPID || ''}] ${item?.Name || ''}`, source: window?.location?.href || 'https://gandpconstruction.com/inventory' });
                setTouched({});
                setFormVisible(false);
            } else {
                messageApi.error('Failed to send message. Please try again.', 5);
            }
        } catch (error) {
            messageApi.error('An error occurred. Please try again.', 5);
        }

        setIsProcessing(false);
    };

    return (
        <div className="w-full h-fit bg-linear-to-br from-gray-50 to-white rounded-xl p-6 shadow-sm">
            <form ref={formRef} onSubmit={submit} className="space-y-5">
                <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Inquire About This Item</h3>
                    <p className="text-gray-500 text-sm">Fill out the form below and we&apos;ll get back to you shortly</p>
                </div>

                {/* Name Field */}
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                        Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        required
                        placeholder="Enter your full name"
                        className={getInputClassName('name', !!errors.name)}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        onBlur={() => handleBlur('name')}
                    />
                    {touched.name && errors.name && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {errors.name}
                        </p>
                    )}
                </div>

                {/* Email Field */}
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                        Email <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        required
                        placeholder="your.email@example.com"
                        className={getInputClassName('email', !!errors.email)}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        onBlur={() => handleBlur('email')}
                    />
                    {touched.email && errors.email && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {errors.email}
                        </p>
                    )}
                </div>

                {/* Phone Field */}
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                        Phone <span className="text-gray-400 text-xs font-normal">(optional)</span>
                    </label>
                    <input
                        type="tel"
                        placeholder="(555) 123-4567"
                        className={getInputClassName('phone', !!errors.phone)}
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        onBlur={() => handleBlur('phone')}
                    />
                    {touched.phone && errors.phone && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {errors.phone}
                        </p>
                    )}
                </div>

                {/* Message Field */}
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                        Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        required
                        rows={4}
                        placeholder="Tell us about your inquiry..."
                        className={`${getInputClassName('message', !!errors.message)} resize-none`}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        onBlur={() => handleBlur('message')}
                    />
                    <div className="flex justify-between items-center">
                        {touched.message && errors.message ? (
                            <p className="text-red-500 text-xs flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {errors.message}
                            </p>
                        ) : <span />}
                        <span className={`text-xs ${formData.message.length < 10 ? 'text-gray-400' : 'text-green-500'}`}>
                            {formData.message.length}/10 min
                        </span>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isProcessing}
                        disabled={!isValid && Object.keys(touched).length > 0}
                        className="flex-1 h-12 text-base font-semibold rounded-lg!"
                    >
                        {isProcessing ? 'Sending...' : 'Send Inquiry'}
                    </Button>
                    <Button
                        onClick={() => setFormVisible(false)}
                        className="h-12 px-6 text-base rounded-lg!"
                    >
                        Cancel
                    </Button>
                </div>

                <p className="text-center text-xs text-gray-400 mt-4">
                    By submitting, you agree to our privacy policy
                </p>
            </form>
        </div>
    );
}